import {
  workOrderAssignmentListQuery,
  WorkOrderAssignmentListItem,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderAssignmentQuery";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
  GetQueryVariablesFn,
} from "@/lib/Components/DataTable/DataTable";
import {
  WorkOrderAssignmentStatus,
  WorkOrderAssignmentListQueryVariables,
  QueryWorkOrderAssignmentsOrderByColumn,
  QueryWorkOrderAssignmentsOrderByRelationOrderByClause,
  QueryWorkOrderAssignmentsOrderByProDriverColumn,
  QueryWorkOrderAssignmentsOrderByWorkOrderColumn,
  QueryWorkOrderAssignmentsOrderByDepartureAddressColumn,
  OrderByRelationWithColumnAggregateFunction,
  SortOrder,
} from "@/gql/graphql";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import {
  MultiSelectFilter,
  MultiSelectFilterOption,
} from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { SupplierFilter } from "@/lib/Components/DataTable/Filters/SupplierFilter";
import { useTranslations } from "use-intl";
import { useUpdateAssignmentStatus } from "@/app/ProDrivers/WorkOrders/Hooks/useUpdateAssignmentStatus";
import { AssignmentStatusBadge } from "@/app/ProDrivers/WorkOrders/Components/AssignmentStatusBadge";
import { CellContext } from "@/lib/Components/DataTable/types";
import { Link } from "@/components/catalyst/link";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import { formatDate } from "@/lib/Formatters/formatDate";
import { Star, Phone, Mail, ArrowRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { GenericErrorBoundary } from "@/lib/Components/Layout/ErrorBoundary/ErrorBoundary";
import { useWorkOrderAssignmentActions } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignmentActions";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { CreateReviewDialog } from "@/lib/Components/Rating/CreateReviewDialog";
import { Highlighter } from "@/lib/Components/Highlighter";

export type AssignmentTableCols =
  | "id"
  | "status"
  | "driver"
  | "work_order"
  | "route"
  | "pickup_date"
  | "delivery_date";

function useAssignmentFilters() {
  const t = useTranslations("workOrder.assignment");

  return [
    {
      label: t("status.ASSIGNED"),
      value: WorkOrderAssignmentStatus.Assigned,
    },
    {
      label: t("status.ON_ROAD"),
      value: WorkOrderAssignmentStatus.OnRoad,
    },
    {
      label: t("status.DELIVERED"),
      value: WorkOrderAssignmentStatus.Delivered,
    },
    {
      label: t("status.RECEIPTS_UPLOADED"),
      value: WorkOrderAssignmentStatus.ReceiptsUploaded,
    },
    {
      label: t("status.RECEIPTS_APPROVED"),
      value: WorkOrderAssignmentStatus.ReceiptsApproved,
    },
    {
      label: t("status.COMPLETED"),
      value: WorkOrderAssignmentStatus.Completed,
    },
  ] satisfies MultiSelectFilterOption<WorkOrderAssignmentStatus>[];
}

export function AssignmentsTable({
  status,
  setStatus,
  filterSupplierId,
  setFilterSupplierId,
  proDriverId,
}: {
  status: WorkOrderAssignmentStatus[];
  setStatus: (status: WorkOrderAssignmentStatus[]) => void;
  filterSupplierId: string | null;
  setFilterSupplierId: (supplierId: string | null) => void;
  proDriverId?: string;
}) {
  "use no memo";
  const t = useTranslations("workOrder.assignment");
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const assignmentFilters = useAssignmentFilters();
  const getActions = useWorkOrderAssignmentActions();

  // Use global supplier if set, otherwise use filter
  const effectiveSupplierId = globalSupplierId ?? filterSupplierId;

  // When scoped to a single driver, the driver column is redundant.
  const scopedToDriver = !!proDriverId;

  const defaultColumns: DataTableColDef<
    WorkOrderAssignmentListItem,
    any,
    AssignmentTableCols
  >[] = [
    {
      id: "driver",
      accessorFn: (data) => data,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("driver")} />
      ),
      cell: DriverCell,
      isHidden: scopedToDriver,
    },
    {
      id: "status",
      accessorFn: (data) => data.status,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("statusLabel")} />
      ),
      cell: StatusCell,
    },
    {
      id: "work_order",
      accessorFn: (data) => data.workOrder.reference,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("workOrder")} />
      ),
      cell: WorkOrderCell,
    },
    {
      id: "route",
      accessorFn: (data) => data,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("route")} />
      ),
      cell: RouteCell,
    },
    {
      id: "pickup_date",
      accessorFn: (data) =>
        data.pickup_date ? formatDate(data.pickup_date) : "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pickup")} />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "delivery_date",
      accessorFn: (data) =>
        data.delivery_date ? formatDate(data.delivery_date) : "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("delivery")} />
      ),
      cell: DeEmphasisedCell,
    },
  ];

  return (
    <GenericErrorBoundary>
      <DataTable
        id={scopedToDriver ? "driver-assignments-table" : "assignments-table"}
        searchable
        accessor={(data) => data.workOrderAssignments}
        columns={defaultColumns}
        document={workOrderAssignmentListQuery}
        initialSorting={[{ id: "work_order", desc: true }]}
        filters={(table) => (
          <div className="flex gap-2">
            <MultiSelectFilter
              label={t("statusLabel")}
              options={assignmentFilters}
              selected={status}
              onChange={(newStatus) => {
                setStatus(newStatus);
                table.setPagination((prev) => ({
                  ...prev,
                  pageIndex: 0,
                }));
              }}
            />
            {!globalSupplierId && !scopedToDriver ? (
              <SupplierFilter
                label={t("supplier")}
                selected={filterSupplierId}
                onChange={(value) => {
                  setFilterSupplierId(value);
                  table.setPagination((prev) => ({
                    ...prev,
                    pageIndex: 0,
                  }));
                }}
              />
            ) : null}
          </div>
        )}
        getQueryVariables={getAssignmentQueryVariables({
          supplierId: effectiveSupplierId,
          status,
          proDriverId,
        })}
        getActions={(model) => getActions(model)}
      />
    </GenericErrorBoundary>
  );
}

function getAssignmentQueryVariables({
  supplierId,
  status,
  proDriverId,
}: {
  supplierId: string | null;
  status: WorkOrderAssignmentStatus[];
  proDriverId?: string;
}): GetQueryVariablesFn<
  WorkOrderAssignmentListQueryVariables,
  AssignmentTableCols
> {
  return ({ pagination, sorting, search }) => {
    const orderBy: QueryWorkOrderAssignmentsOrderByRelationOrderByClause[] = [];

    for (const sort of sorting) {
      const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;

      if (sort.id === "pickup_date") {
        orderBy.push({
          column: QueryWorkOrderAssignmentsOrderByColumn.PickupDate,
          order,
        });
      } else if (sort.id === "delivery_date") {
        orderBy.push({
          column: QueryWorkOrderAssignmentsOrderByColumn.DeliveryDate,
          order,
        });
      } else if (sort.id === "status") {
        orderBy.push({
          column: QueryWorkOrderAssignmentsOrderByColumn.Status,
          order,
        });
      } else if (sort.id === "driver") {
        orderBy.push({
          proDriver: {
            column: QueryWorkOrderAssignmentsOrderByProDriverColumn.Name,
            aggregate: OrderByRelationWithColumnAggregateFunction.Min,
          },
          order,
        });
      } else if (sort.id === "work_order") {
        orderBy.push({
          workOrder: {
            column: QueryWorkOrderAssignmentsOrderByWorkOrderColumn.Reference,
            aggregate: OrderByRelationWithColumnAggregateFunction.Min,
          },
          order,
        });
      } else if (sort.id === "route") {
        orderBy.push({
          departureAddress: {
            column: QueryWorkOrderAssignmentsOrderByDepartureAddressColumn.City,
            aggregate: OrderByRelationWithColumnAggregateFunction.Min,
          },
          order,
        });
      }
    }

    return {
      page: pagination.pageIndex,
      first: pagination.pageSize,
      search: search || undefined,
      supplier_id: supplierId ?? undefined,
      pro_driver_id: proDriverId,
      status: status.length > 0 ? status : undefined,
      orderBy: orderBy.length > 0 ? orderBy : undefined,
    };
  };
}

function DriverCell({
  row,
  table,
}: CellContext<WorkOrderAssignmentListItem, any>) {
  "use no memo";
  const { open } = useDialog(CreateReviewDialog);
  const assignment = row.original;
  const driver = assignment.proDriver;
  const search = table.options.meta?.search ?? "";

  const canRate =
    (assignment.status === WorkOrderAssignmentStatus.Delivered ||
      assignment.status === WorkOrderAssignmentStatus.ReceiptsUploaded ||
      assignment.status === WorkOrderAssignmentStatus.Completed) &&
    !assignment.review;

  return (
    <div className="flex items-center gap-3">
      <Avatar
        className="size-9 bg-gray-200 outline -outline-offset-1 outline-black/5"
        initials={getInitials(driver.name)}
      />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <CopyToClipboard text={driver.name}>
            <span className="truncate font-medium text-gray-900">
              <Highlighter searchString={search}>
                {driver.name ?? ""}
              </Highlighter>
            </span>
          </CopyToClipboard>
          {driver.rating ? (
            <div className="flex items-center gap-0.5 text-xs text-amber-600">
              <Star className="h-3 w-3 fill-current" />
              <span>{driver.rating.toFixed(1)}</span>
            </div>
          ) : null}
        </div>
        {driver.email ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Mail className="h-3 w-3" />
                <CopyToClipboard text={driver.email}>
                  <span className="max-w-[120px] truncate">
                    <Highlighter searchString={search}>
                      {driver.email}
                    </Highlighter>
                  </span>
                </CopyToClipboard>
              </div>
            </TooltipTrigger>
            <TooltipContent>{driver.email}</TooltipContent>
          </Tooltip>
        ) : null}
        {driver.phone ? (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Phone className="h-3 w-3" />
            <CopyToClipboard text={driver.phone}>
              <Highlighter searchString={search}>{driver.phone}</Highlighter>
            </CopyToClipboard>
          </div>
        ) : null}
        {canRate ? (
          <button
            type="button"
            className="mt-1 flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
            onClick={(e) => {
              e.stopPropagation();
              open({
                assignmentId: assignment.id,
                proDriverId: assignment.pro_driver_id,
              });
            }}
          >
            <Star className="h-3 w-3" />
            Rate driver
          </button>
        ) : null}
      </div>
    </div>
  );
}

function WorkOrderCell({
  row,
  table,
}: CellContext<WorkOrderAssignmentListItem, any>) {
  "use no memo";
  const workOrder = row.original.workOrder;
  const search = table.options.meta?.search ?? "";

  return (
    <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
      <Link
        from="/$tenant/$orgId/supplier/{-$supplierId}"
        to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
        params={(prev) => ({ ...prev, id: workOrder.id })}
        className="font-medium text-blue-600 hover:text-blue-800"
      >
        <Highlighter searchString={search}>{workOrder.reference}</Highlighter>
      </Link>
      <div className="text-xs text-gray-500">
        <Highlighter searchString={search}>
          {workOrder.supplier.name}
        </Highlighter>
      </div>
    </div>
  );
}

function RouteCell({
  row,
  table,
}: CellContext<WorkOrderAssignmentListItem, any>) {
  "use no memo";
  const workOrder = row.original.workOrder;
  const search = table.options.meta?.search ?? "";

  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <span className="truncate">
        <Highlighter searchString={search}>
          {workOrder.departureAddress?.city ?? ""}
        </Highlighter>
      </span>
      <ArrowRight className="h-3 w-3 shrink-0 text-gray-400" />
      <span className="truncate">
        <Highlighter searchString={search}>
          {workOrder.deliveryAddress?.city ?? ""}
        </Highlighter>
      </span>
    </div>
  );
}

function StatusCell({ row }: CellContext<WorkOrderAssignmentListItem, any>) {
  "use no memo";
  const { handleStatusChange, isPending } = useUpdateAssignmentStatus(
    row.original.id,
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <AssignmentStatusBadge
        status={row.original.status}
        onStatusChange={handleStatusChange}
        disabled={isPending}
      />
    </div>
  );
}
